import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const formatOMR = (val) => {
  if (!val && val !== 0) return 'OMR 0';
  if (val >= 1_000_000) return `OMR ${(val / 1_000_000)?.toFixed(1)}M`;
  if (val >= 1_000) return `OMR ${(val / 1_000)?.toFixed(0)}K`;
  return `OMR ${val?.toLocaleString()}`;
};

const getGreeting = () => {
  const h = new Date()?.getHours();
  if (h < 12) return 'Good Morning';
  if (h < 17) return 'Good Afternoon';
  return 'Good Evening';
};

const healthColor = (score) => {
  if (score >= 70) return { bg: 'bg-emerald-50', border: 'border-emerald-200', dot: 'bg-emerald-500', text: 'text-emerald-700', label: 'Healthy' };
  if (score >= 40) return { bg: 'bg-amber-50', border: 'border-amber-200', dot: 'bg-amber-500', text: 'text-amber-700', label: 'At Risk' };
  return { bg: 'bg-red-50', border: 'border-red-200', dot: 'bg-red-500', text: 'text-red-700', label: 'Critical' };
};

const tierBadge = (tier) => {
  const map = {
    platinum: 'bg-slate-800 text-white',
    gold: 'bg-amber-500 text-white',
    silver: 'bg-slate-400 text-white',
    bronze: 'bg-orange-700 text-white',
  };
  return map?.[tier?.toLowerCase?.()] || 'bg-slate-200 text-slate-700';
};

// ─── KPI Card ─────────────────────────────────────────────────────────────────
const KpiCard = ({
  icon,
  label,
  value,
  sub,
  trend = undefined,
  color = 'from-primary to-secondary',
  onClick,
  ctaLabel,
  ctaPath,
  isZero = false,
}) => {
  const [hovered, setHovered] = useState(false);

  const handleClick = () => {
    if (onClick) onClick();
  };

  return (
    <div
      className={`relative bg-white rounded-2xl border border-slate-100 shadow-sm p-6 flex flex-col gap-4 min-h-[200px] transition-all duration-200 select-none
        ${onClick ? 'cursor-pointer hover:shadow-lg hover:-translate-y-0.5 hover:border-slate-200' : ''}
      `}
      onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="flex items-center justify-between">
        <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${color} flex items-center justify-center transition-all duration-200 ${hovered ? 'brightness-110 scale-105' : ''}`}>
          <Icon name={icon} size={28} className="text-white" />
        </div>
        {trend !== undefined && (
          <span className={`flex items-center gap-1 text-sm font-semibold px-3 py-1 rounded-full ${trend >= 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
            <Icon name={trend >= 0 ? 'TrendingUp' : 'TrendingDown'} size={14} />
            {Math.abs(trend)}%
          </span>
        )}
      </div>

      <div className="mt-2">
        <div className="text-4xl font-bold text-slate-900 font-heading leading-tight">{value}</div>
        <div className="text-xs font-semibold text-slate-500 mt-3 uppercase tracking-wider">{label}</div>
        {sub && <div className="text-sm text-slate-400 mt-1">{sub}</div>}
      </div>

      {/* Zero-value CTA */}
      {isZero && ctaLabel && ctaPath && (
        <div className="mt-auto pt-2">
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:text-secondary transition-colors">
            <Icon name="Plus" size={12} />
            {ctaLabel}
          </span>
        </div>
      )}

      {/* View Details label — fades in on hover */}
      {onClick && !isZero && (
        <div className={`absolute bottom-4 right-5 flex items-center gap-1 text-xs font-semibold text-primary transition-all duration-200 ${hovered ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-2'}`}>
          View Details <Icon name="ArrowRight" size={12} />
        </div>
      )}
    </div>
  );
};

// ─── Section Header ───────────────────────────────────────────────────────────
const SectionHeader = ({ icon, title, action, onAction }) => (
  <div className="flex items-center justify-between mb-16">
    <div className="flex items-center gap-10">
      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center">
        <Icon name={icon} size={16} className="text-primary" />
      </div>
      <h2 className="text-base font-bold text-slate-800">{title}</h2>
    </div>
    {action && (
      <button onClick={onAction} className="text-xs font-semibold text-primary hover:text-secondary transition-colors flex items-center gap-4">
        {action} <Icon name="ArrowRight" size={12} />
      </button>
    )}
  </div>
);

// ─── Alert Badge ──────────────────────────────────────────────────────────────
const AlertBadge = ({ type, message }) => {
  const styles = {
    critical: { bg: 'bg-red-50 border-red-200', icon: 'AlertCircle', iconColor: 'text-red-500', text: 'text-red-800' },
    warning: { bg: 'bg-amber-50 border-amber-200', icon: 'AlertTriangle', iconColor: 'text-amber-500', text: 'text-amber-800' },
    info: { bg: 'bg-yellow-50 border-yellow-200', icon: 'Clock', iconColor: 'text-yellow-600', text: 'text-yellow-800' },
    success: { bg: 'bg-emerald-50 border-emerald-200', icon: 'CheckCircle', iconColor: 'text-emerald-500', text: 'text-emerald-800' },
  };
  const s = styles?.[type] || styles?.info;
  return (
    <div className={`flex items-start gap-10 p-12 rounded-xl border ${s?.bg} mb-8`}>
      <Icon name={s?.icon} size={15} className={`${s?.iconColor} flex-shrink-0 mt-1`} />
      <span className={`text-xs font-medium ${s?.text} leading-relaxed`}>{message}</span>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ExecutiveDashboard = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();

  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  // Data state
  const [partners, setPartners] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tenders, setTenders] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [trainingAssignments, setTrainingAssignments] = useState([]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const results = await Promise.all([
        supabase?.from('partners')?.select('id, legal_name, brand_name, tier, health_score, status, created_at, partnership_start_date'),
        supabase?.from('opportunities')?.select('id, name, value, opp_status, expected_close, created_at, partner_id, partners(brand_name, legal_name)'),
        supabase?.from('projects')?.select('id, name, project_status, end_date, created_at, partner_id, partners(brand_name, legal_name)'),
        supabase?.from('tenders')?.select('id, name, tender_status, submission_deadline, created_at, partner_id, partners(brand_name, legal_name)'),
        supabase?.from('ecity_certifications')?.select('id, partner_id, cert_name, expiry_date, status, issued_date, partners(brand_name, legal_name)'),
        supabase?.from('partner_training_assignments')?.select('id, partner_id, status, completed_at, partners(brand_name, legal_name)'),
      ]);

      const partnersData = results?.[0]?.data;
      const oppsData = results?.[1]?.data;
      const projectsData = results?.[2]?.data;
      const tendersData = results?.[3]?.data;
      const certsData = results?.[4]?.data;
      const trainingData = results?.[5]?.data;

      setPartners(partnersData || []);
      setOpportunities(oppsData || []);
      setProjects(projectsData || []);
      setTenders(tendersData || []);
      setCertifications(certsData || []);
      setTrainingAssignments(trainingData || []);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Executive Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ─── Derived KPIs ──────────────────────────────────────────────────────────
  const activePartners = partners?.filter(p => p?.status === 'active');
  const inactivePartners = partners?.filter(p => p?.status !== 'active');

  const totalPipelineValue = opportunities?.filter(o => !['Won', 'Lost']?.includes(o?.opp_status))?.reduce((sum, o) => sum + (parseFloat(o?.value) || 0), 0);

  const wonOpps = opportunities?.filter(o => o?.opp_status === 'Won');
  const closedOpps = opportunities?.filter(o => ['Won', 'Lost']?.includes(o?.opp_status));
  const winRate = closedOpps?.length > 0 ? Math.round((wonOpps?.length / closedOpps?.length) * 100) : 0;

  const activeEngagements = [
    ...opportunities?.filter(o => !['Won', 'Lost']?.includes(o?.opp_status)),
    ...projects?.filter(p => p?.project_status === 'In Progress'),
    ...tenders?.filter(t => t?.tender_status === 'Open'),
  ]?.length;

  const activeCerts = certifications?.filter(c => c?.status === 'active')?.length;

  const avgHealthScore = partners?.length > 0
    ? Math.round(partners?.reduce((sum, p) => sum + (p?.health_score || 0), 0) / partners?.length)
    : 0;

  // ─── Pipeline by Stage ─────────────────────────────────────────────────────
  const stages = ['Identified', 'Qualified', 'Proposal', 'Won'];
  const pipelineByStage = stages?.map(stage => ({
    name: stage,
    value: opportunities?.filter(o => o?.opp_status === stage)?.reduce((s, o) => s + (parseFloat(o?.value) || 0), 0),
    count: opportunities?.filter(o => o?.opp_status === stage)?.length,
  }));

  // ─── Revenue vs Target (quarterly mock targets with real won data) ──────────
  const now = new Date();
  const currentYear = now?.getFullYear();
  const quarterlyRevenue = [1, 2, 3, 4]?.map(q => {
    const qStart = new Date(currentYear, (q - 1) * 3, 1);
    const qEnd = new Date(currentYear, q * 3, 0);
    const actual = wonOpps?.filter(o => {
        const d = new Date(o.expected_close || o.created_at);
        return d >= qStart && d <= qEnd;
      })?.reduce((s, o) => s + (parseFloat(o?.value) || 0), 0);
    return { quarter: `Q${q} ${currentYear}`, actual, target: 500000 + q * 100000 };
  });

  // ─── Recent Wins (last 30 days) ────────────────────────────────────────────
  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
  const recentWins = wonOpps?.filter(o => new Date(o.expected_close || o.created_at) >= thirtyDaysAgo)?.slice(0, 5);

  // ─── Upcoming Deadlines ────────────────────────────────────────────────────
  const sevenDaysFromNow = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
  const upcomingTenders = tenders?.filter(t => {
    const d = new Date(t.submission_deadline);
    return t?.tender_status === 'Open' && d <= sevenDaysFromNow && d >= new Date();
  });
  const overdueProjects = projects?.filter(p => {
    const d = new Date(p.end_date);
    return p?.project_status === 'In Progress' && d < new Date();
  });

  // ─── New Partners this month ───────────────────────────────────────────────
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const newPartnersThisMonth = partners?.filter(p => new Date(p.created_at) >= monthStart);

  // ─── Top 5 Partners by Health Score ───────────────────────────────────────
  const top5Partners = [...partners]?.filter(p => p?.health_score != null)?.sort((a, b) => (b?.health_score || 0) - (a?.health_score || 0))?.slice(0, 5);

  // ─── Certifications expiring in 30 days ───────────────────────────────────
  const expiringCerts = certifications?.filter(c => {
    if (!c?.expiry_date) return false;
    const d = new Date(c.expiry_date);
    return d >= new Date() && d <= new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
  });

  // ─── Training completion rate ──────────────────────────────────────────────
  const quarterStart = new Date(currentYear, Math.floor(now.getMonth() / 3) * 3, 1);
  const quarterAssignments = trainingAssignments?.filter(t => new Date(t.completed_at || t.created_at) >= quarterStart);
  const completedTraining = quarterAssignments?.filter(t => t?.status === 'completed')?.length;
  const trainingCompletionRate = quarterAssignments?.length > 0
    ? Math.round((completedTraining / quarterAssignments?.length) * 100)
    : 0;

  // ─── Strategic Alerts ─────────────────────────────────────────────────────
  const belowThreshold = partners?.filter(p => (p?.health_score || 0) < 40);
  const stalOpps = opportunities?.filter(o => {
    if (['Won', 'Lost']?.includes(o?.opp_status)) return false;
    const d = new Date(o.created_at);
    return (Date.now() - d?.getTime()) > 30 * 24 * 60 * 60 * 1000;
  });
  const upgradeReady = partners?.filter(p => (p?.health_score || 0) >= 85 && p?.tier !== 'platinum');

  const FUNNEL_COLORS = ['#6366f1', '#8b5cf6', '#a78bfa', '#c4b5fd'];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-16">
          <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
          <p className="text-sm text-slate-500 font-medium">Loading Executive Dashboard…</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Executive Dashboard — PartnerHub</title>
      </Helmet>

      <div className="min-h-screen bg-slate-50">
        <Header />
        {/* ── Section 1: Executive Header ── */}
        <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white mt-[148px]">
          <div className="max-w-[1600px] mx-auto px-24 py-28">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-20">
              {/* Left: Welcome */}
              <div>
                <h1 className="text-3xl font-bold font-heading leading-tight">
                  {getGreeting()},{' '}
                  <span className="bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
                    {userProfile?.full_name || user?.email?.split('@')?.[0] || 'Executive'}
                  </span>
                </h1>
                <p className="text-slate-400 text-sm mt-6">
                  {new Date()?.toLocaleDateString('en-OM', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
                <p className="text-slate-500 text-xs mt-4 flex items-center gap-6">
                  <Icon name="RefreshCw" size={11} />
                  Last updated: {lastUpdated?.toLocaleTimeString()}
                </p>
              </div>

              {/* Right: Quick Actions */}
              <div className="flex flex-wrap gap-10">
                <button
                  onClick={() => navigate('/reporting-export')}
                  className="flex items-center gap-8 px-16 py-10 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl text-sm font-semibold transition-all duration-200"
                >
                  <Icon name="Download" size={15} /> Export Report
                </button>
                <button
                  onClick={() => navigate('/partner-registration-dashboard')}
                  className="flex items-center gap-8 px-16 py-10 bg-primary hover:bg-primary/90 rounded-xl text-sm font-semibold transition-all duration-200 shadow-lg"
                >
                  <Icon name="UserPlus" size={15} /> Add Partner
                </button>
                <button
                  onClick={fetchAll}
                  className="flex items-center gap-8 px-16 py-10 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl text-sm font-semibold transition-all duration-200"
                >
                  <Icon name="RefreshCw" size={15} /> Refresh
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-[1600px] mx-auto px-24 py-28 space-y-32">

          {/* ── Section 2: Top KPI Row ── */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-16">
            <KpiCard
              icon="Users"
              label="Total Partners"
              value={partners?.length}
              sub={`${activePartners?.length} active · ${inactivePartners?.length} inactive`}
              color="from-blue-500 to-blue-600"
              trend={0}
              onClick={() => navigate('/all-partners')}
              isZero={partners?.length === 0}
              ctaLabel="Add First Partner"
              ctaPath="/partner-registration-dashboard"
            />
            <KpiCard
              icon="DollarSign"
              label="Pipeline Value"
              value={formatOMR(totalPipelineValue)}
              sub="Active pipeline"
              color="from-violet-500 to-violet-600"
              onClick={() => navigate('/engagement-records', { state: { statusFilter: 'active_pipeline' } })}
              isZero={totalPipelineValue === 0}
              ctaLabel="Add First Opportunity"
              ctaPath="/engagement-management-hub"
            />
            <KpiCard
              icon="Target"
              label="Win Rate"
              value={`${winRate}%`}
              sub={`${wonOpps?.length} of ${closedOpps?.length} closed`}
              trend={winRate - 50}
              color="from-emerald-500 to-emerald-600"
              onClick={() => navigate('/engagement-records', { state: { statusFilter: 'Won' } })}
              isZero={closedOpps?.length === 0}
              ctaLabel="Add First Opportunity"
              ctaPath="/engagement-management-hub"
            />
            <KpiCard
              icon="Activity"
              label="Active Engagements"
              value={activeEngagements}
              sub="Opps + Projects + Tenders"
              color="from-orange-500 to-orange-600"
              trend={0}
              onClick={() => navigate('/engagement-records', { state: { statusFilter: 'In Progress' } })}
              isZero={activeEngagements === 0}
              ctaLabel="Add First Engagement"
              ctaPath="/engagement-management-hub"
            />
            <KpiCard
              icon="Zap"
              label="Avg Health Score"
              value={`${avgHealthScore}/100`}
              sub="Across all partners"
              trend={avgHealthScore - 60}
              color="from-teal-500 to-teal-600"
              onClick={() => navigate('/all-partners', { state: { sortBy: 'health_score' } })}
              isZero={partners?.length === 0}
              ctaLabel="Add First Partner"
              ctaPath="/partner-registration-dashboard"
            />
          </div>

          {/* ── Section 3: Partner Health Map ── */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-24">
            <SectionHeader
              icon="Map"
              title="Partner Health Map"
              action="View All Partners"
              onAction={() => navigate('/all-partners')}
            />
            {partners?.length === 0 ? (
              <div className="text-center py-32 text-slate-400 text-sm">No partners found.</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-12">
                {partners?.map(partner => {
                  const hc = healthColor(partner?.health_score || 0);
                  return (
                    <button
                      key={partner?.id}
                      onClick={() => navigate(`/partner-profile-portal/${partner?.id}`)}
                      className={`${hc?.bg} ${hc?.border} border rounded-xl p-14 text-left hover:shadow-md transition-all duration-200 group`}
                    >
                      <div className="flex items-center justify-between mb-8">
                        <span className={`text-xs font-bold px-6 py-2 rounded-full ${tierBadge(partner?.tier)} capitalize`}>
                          {partner?.tier || 'N/A'}
                        </span>
                        <div className={`w-8 h-8 rounded-full ${hc?.dot}`} />
                      </div>
                      <div className="text-xs font-bold text-slate-800 leading-tight truncate group-hover:text-primary transition-colors">
                        {partner?.brand_name || partner?.legal_name}
                      </div>
                      <div className={`text-xs font-semibold mt-4 ${hc?.text}`}>{hc?.label}</div>
                      <div className="text-xs text-slate-400 mt-2">Score: {partner?.health_score ?? '—'}</div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── Section 4: Pipeline & Revenue ── */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-24">
            {/* Pipeline by Stage */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-24">
              <SectionHeader
                icon="Filter"
                title="Pipeline by Stage"
                action="View Engagements"
                onAction={() => navigate('/engagement-management-hub')}
              />
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={pipelineByStage} layout="vertical" margin={{ left: 10, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                  <XAxis type="number" tickFormatter={v => formatOMR(v)} tick={{ fontSize: 10, fill: '#94a3b8' }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: '#475569', fontWeight: 600 }} width={80} />
                  <Tooltip
                    formatter={(v) => [formatOMR(v), 'Pipeline Value']}
                    contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12 }}
                  />
                  <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                    {pipelineByStage?.map((_, i) => (
                      <Cell key={i} fill={FUNNEL_COLORS?.[i % FUNNEL_COLORS?.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="flex gap-12 mt-12 flex-wrap">
                {pipelineByStage?.map((s, i) => (
                  <div key={s?.name} className="flex items-center gap-6 text-xs text-slate-500">
                    <div className="w-8 h-8 rounded-full" style={{ background: FUNNEL_COLORS?.[i % FUNNEL_COLORS?.length] }} />
                    {s?.name}: {s?.count} deals
                  </div>
                ))}
              </div>
            </div>

            {/* Revenue vs Target */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-24">
              <SectionHeader
                icon="BarChart3"
                title="Revenue vs Target by Quarter"
                action="View Reports"
                onAction={() => navigate('/reporting-export')}
              />
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={quarterlyRevenue} margin={{ left: 10, right: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="quarter" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                  <YAxis tickFormatter={v => formatOMR(v)} tick={{ fontSize: 10, fill: '#94a3b8' }} />
                  <Tooltip
                    formatter={(v, name) => [formatOMR(v), name === 'actual' ? 'Actual' : 'Target']}
                    contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12 }}
                  />
                  <Bar dataKey="actual" fill="#6366f1" radius={[4, 4, 0, 0]} name="actual" />
                  <Bar dataKey="target" fill="#e2e8f0" radius={[4, 4, 0, 0]} name="target" />
                </BarChart>
              </ResponsiveContainer>
              <div className="flex gap-16 mt-12">
                <div className="flex items-center gap-6 text-xs text-slate-500">
                  <div className="w-10 h-3 rounded-full bg-indigo-500" /> Actual Revenue
                </div>
                <div className="flex items-center gap-6 text-xs text-slate-500">
                  <div className="w-10 h-3 rounded-full bg-slate-200" /> Target
                </div>
              </div>
            </div>
          </div>

          {/* ── Section 5: Engagement Activity ── */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-24">
            <SectionHeader
              icon="Activity"
              title="Engagement Activity"
              action="View All Engagements"
              onAction={() => navigate('/engagement-management-hub')}
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-20">
              {/* Recent Wins */}
              <div>
                <div className="flex items-center gap-8 mb-12">
                  <div className="w-6 h-6 rounded-full bg-emerald-500" />
                  <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">Recent Wins (30 days)</span>
                </div>
                {recentWins?.length === 0 ? (
                  <p className="text-xs text-slate-400 py-8">No wins in the last 30 days.</p>
                ) : (
                  <div className="space-y-8">
                    {recentWins?.map(o => (
                      <div key={o?.id} className="flex items-center justify-between p-10 bg-emerald-50 rounded-xl border border-emerald-100">
                        <div>
                          <div className="text-xs font-semibold text-slate-800 truncate max-w-[140px]">{o?.name}</div>
                          <div className="text-xs text-slate-400">{o?.partners?.brand_name || o?.partners?.legal_name}</div>
                        </div>
                        <div className="text-xs font-bold text-emerald-700">{formatOMR(o?.value)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Upcoming Deadlines */}
              <div>
                <div className="flex items-center gap-8 mb-12">
                  <div className="w-6 h-6 rounded-full bg-amber-500" />
                  <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">Upcoming Deadlines</span>
                </div>
                {upcomingTenders?.length === 0 && overdueProjects?.length === 0 ? (
                  <p className="text-xs text-slate-400 py-8">No urgent deadlines.</p>
                ) : (
                  <div className="space-y-8">
                    {upcomingTenders?.map(t => (
                      <div key={t?.id} className="flex items-center justify-between p-10 bg-amber-50 rounded-xl border border-amber-100">
                        <div>
                          <div className="text-xs font-semibold text-slate-800 truncate max-w-[140px]">{t?.name}</div>
                          <div className="text-xs text-amber-600">Tender · Due {new Date(t.submission_deadline)?.toLocaleDateString()}</div>
                        </div>
                        <Icon name="Clock" size={14} className="text-amber-500" />
                      </div>
                    ))}
                    {overdueProjects?.map(p => (
                      <div key={p?.id} className="flex items-center justify-between p-10 bg-red-50 rounded-xl border border-red-100">
                        <div>
                          <div className="text-xs font-semibold text-slate-800 truncate max-w-[140px]">{p?.name}</div>
                          <div className="text-xs text-red-600">Project · Overdue</div>
                        </div>
                        <Icon name="AlertCircle" size={14} className="text-red-500" />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* New Partners */}
              <div>
                <div className="flex items-center gap-8 mb-12">
                  <div className="w-6 h-6 rounded-full bg-blue-500" />
                  <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">New Partners This Month</span>
                </div>
                {newPartnersThisMonth?.length === 0 ? (
                  <p className="text-xs text-slate-400 py-8">No new partners this month.</p>
                ) : (
                  <div className="space-y-8">
                    {newPartnersThisMonth?.slice(0, 5)?.map(p => (
                      <div key={p?.id} className="flex items-center gap-10 p-10 bg-blue-50 rounded-xl border border-blue-100">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                          {(p?.brand_name || p?.legal_name || 'P')?.charAt(0)?.toUpperCase()}
                        </div>
                        <div>
                          <div className="text-xs font-semibold text-slate-800">{p?.brand_name || p?.legal_name}</div>
                          <div className="text-xs text-slate-400 capitalize">{p?.tier || 'Untiered'}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Section 6: Enablement Snapshot ── */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-24">
            <SectionHeader
              icon="GraduationCap"
              title="Enablement Snapshot"
              action="View Enablement Center"
              onAction={() => navigate('/partner-enablement-center')}
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-20">
              {/* Top 5 by Health Score */}
              <div>
                <div className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-12">Top 5 Partners by Health Score</div>
                {top5Partners?.length === 0 ? (
                  <p className="text-xs text-slate-400">No data available.</p>
                ) : (
                  <div className="space-y-8">
                    {top5Partners?.map((p, i) => (
                      <div key={p?.id} className="flex items-center gap-10">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                          {i + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-semibold text-slate-800 truncate">{p?.brand_name || p?.legal_name}</div>
                          <div className="w-full bg-slate-100 rounded-full h-4 mt-4">
                            <div
                              className="bg-gradient-to-r from-primary to-secondary h-4 rounded-full transition-all duration-500"
                              style={{ width: `${p?.health_score || 0}%` }}
                            />
                          </div>
                        </div>
                        <div className="text-xs font-bold text-slate-700 w-24 text-right">{p?.health_score ?? '—'}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Expiring Certifications */}
              <div>
                <div className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-12">Certifications Expiring (30 days)</div>
                {expiringCerts?.length === 0 ? (
                  <p className="text-xs text-slate-400 py-8">No certifications expiring soon.</p>
                ) : (
                  <div className="space-y-8">
                    {expiringCerts?.slice(0, 5)?.map(c => (
                      <div key={c?.id} className="flex items-center justify-between p-10 bg-orange-50 rounded-xl border border-orange-100">
                        <div>
                          <div className="text-xs font-semibold text-slate-800 truncate max-w-[140px]">{c?.cert_name}</div>
                          <div className="text-xs text-slate-400">{c?.partners?.brand_name || c?.partners?.legal_name}</div>
                        </div>
                        <div className="text-xs font-bold text-orange-600">
                          {new Date(c.expiry_date)?.toLocaleDateString()}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Training Completion */}
              <div>
                <div className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-12">Training Completion (This Quarter)</div>
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="relative w-28 h-28">
                    <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                      <circle cx="50" cy="50" r="40" fill="none" stroke="#f1f5f9" strokeWidth="10" />
                      <circle
                        cx="50" cy="50" r="40" fill="none"
                        stroke="url(#grad)" strokeWidth="10"
                        strokeDasharray={`${2 * Math.PI * 40 * trainingCompletionRate / 100} ${2 * Math.PI * 40}`}
                        strokeLinecap="round"
                      />
                      <defs>
                        <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#6366f1" />
                          <stop offset="100%" stopColor="#8b5cf6" />
                        </linearGradient>
                      </defs>
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-xl font-bold text-slate-900">{trainingCompletionRate}%</span>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-10 text-center">
                    {completedTraining} of {quarterAssignments?.length} assignments completed
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* ── Section 7: Strategic Alerts ── */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-24">
            <SectionHeader
              icon="Bell"
              title="Strategic Alerts"
              action=""
              onAction={() => {}}
            />
            {belowThreshold?.length === 0 && upcomingTenders?.length === 0 && stalOpps?.length === 0 && upgradeReady?.length === 0 ? (
              <div className="flex items-center gap-12 p-16 bg-emerald-50 rounded-xl border border-emerald-100">
                <Icon name="CheckCircle" size={18} className="text-emerald-500" />
                <span className="text-sm font-medium text-emerald-700">All systems healthy — no strategic alerts at this time.</span>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-24">
                {belowThreshold?.length > 0 && (
                  <AlertBadge
                    type="critical"
                    message={`${belowThreshold?.length} partner${belowThreshold?.length > 1 ? 's' : ''} below capability threshold (health score < 40): ${belowThreshold?.slice(0, 3)?.map(p => p?.brand_name || p?.legal_name)?.join(', ')}${belowThreshold?.length > 3 ? ` +${belowThreshold?.length - 3} more` : ''}`}
                  />
                )}
                {upcomingTenders?.length > 0 && (
                  <AlertBadge
                    type="warning"
                    message={`${upcomingTenders?.length} tender${upcomingTenders?.length > 1 ? 's' : ''} closing within 7 days — immediate action required.`}
                  />
                )}
                {stalOpps?.length > 0 && (
                  <AlertBadge
                    type="info"
                    message={`${stalOpps?.length} opportunit${stalOpps?.length > 1 ? 'ies' : 'y'} stale for 30+ days — review and update pipeline status.`}
                  />
                )}
                {upgradeReady?.length > 0 && (
                  <AlertBadge
                    type="success"
                    message={`${upgradeReady?.length} partner${upgradeReady?.length > 1 ? 's' : ''} ready for tier upgrade (health score ≥ 85): ${upgradeReady?.slice(0, 3)?.map(p => p?.brand_name || p?.legal_name)?.join(', ')}`}
                  />
                )}
              </div>
            )}
          </div>

        </div>
      </div>
    </>
  );
};

export default ExecutiveDashboard;
